/**
 * Automated error triage — every server-side exception is fingerprinted, and the
 * first occurrence of each distinct fingerprint is classified by a cheap model
 * into one of three buckets. Recurrences never re-classify; they only bump a
 * counter (and, past the first, append to the linked task's Recurrence subtask).
 */

/** Where in the stack the error was captured. */
export enum ErrorTriageSource {
  /** Thrown by a Base service, caught by `runService`'s central catch. */
  Service = 'service',
  /** A non-Exception error rendered by `renderError` (express path). */
  Request = 'request',
  /** Process-level `uncaughtException`. */
  UncaughtException = 'uncaughtException',
  /** Process-level `unhandledRejection`. */
  UnhandledRejection = 'unhandledRejection',
  /** An explicit `sendFailedAlert` for something no capture point saw (cron partial failures, gates, handled errors). */
  Alert = 'alert',
  /** A browser-side failure reported through `/log-error`. */
  Client = 'client',
}

/**
 * What a person has decided about a fingerprint, as the Error Center shows it.
 * Derived from `resolvedAt` / `suppressed`, never stored — `status` is the
 * classifier's own lifecycle and doubles as its lock, so it can't carry this.
 */
export enum ErrorTriageState {
  Open = 'open',
  Resolved = 'resolved',
  Muted = 'muted',
}

/** A background Arc investigation of one fingerprint; its findings post back onto the row. */
export enum ErrorTriageInvestigationStatus {
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
}

/** The last `sendFailedAlert` that landed on a row. */
export interface IErrorTriageAlertContext {
  title: string
  params: Record<string, unknown>
  userEmail: string | null
  grafanaLink: string | null
  at: string
}

export interface IErrorTriageListItem {
  id: string
  fingerprint: string
  errorName: string
  message: string | null
  origin: string
  source: ErrorTriageSource
  errorCode: string | null
  status: ErrorTriageStatus
  classification: ErrorTriageClassification | null
  state: ErrorTriageState
  occurrenceCount: number
  firstSeenAt: string
  lastSeenAt: string
  resolvedAt: string | null
  reopenedAt: string | null
  reopenCount: number
  suppressed: boolean
  lastAlertTitle: string | null
  asanaTaskUrl: string | null
  /** The linked ACT task. Independent of the Asana link — either, both or neither. */
  taskId: string | null
  /** The linked ACT task is assigned to Dwight Bot: Auto-fix has handed it over. */
  isWithDwightBot: boolean
  investigationStatus: ErrorTriageInvestigationStatus | null
  hasStack: boolean
}

export interface IErrorTriageDetail extends IErrorTriageListItem {
  stack: string | null
  rootCauseName: string | null
  rootCauseMessage: string | null
  rootCauseStack: string | null
  reasoning: string | null
  confidence: string | null
  suspectedFiles: string[]
  lastAlert: IErrorTriageAlertContext | null
  resolvedByUserName: string | null
  triageError: string | null
  investigationResult: string | null
  investigationStartedAt: string | null
  investigationCompletedAt: string | null
  investigationStartedByUserName: string | null
  /** Same flag as the list's — so a drawer opened from a deep link knows it too. */
  isTriageEnabled: boolean
}

/** Lifecycle of a single fingerprint's triage. */
export enum ErrorTriageStatus {
  /** Seen, queued, not yet classified. */
  Pending = 'pending',
  /**
   * Claimed by a worker that is mid-classification. Claiming is an atomic
   * conditional UPDATE, so it doubles as the lock that stops two workers
   * classifying the same fingerprint — and creating two Asana tasks for it.
   * A row stuck here (the worker died) is reclaimed by the sweep once stale,
   * or marked `failed` if that was its final attempt.
   */
  Classifying = 'classifying',
  /** The model returned a bucket and the side effect was applied. */
  Classified = 'classified',
  /**
   * The triage job failed (model call, code lookup, or Asana write). Retried by
   * the sweep until `attemptCount` hits `MAX_TRIAGE_ATTEMPTS`, because the
   * usual cause is transient — a rate limit or a timeout during the very
   * incident that produced the error.
   *
   * The original error's normal logging + alert are untouched either way:
   * a failed triage never silences anything.
   */
  Failed = 'failed',
}

/**
 * The model's three buckets, plus `Incident`, which only the deterministic
 * pre-classifier assigns. The model cannot return null.
 */
export enum ErrorTriageClassification {
  /** A real defect. Logs a bot task into the Ready-for-AI pipeline. */
  Fixable = 'fixable',
  /** Expected behavior, user error, or transient infra. Suppressed. */
  Expected = 'expected',
  /** Genuinely unclear. Logs a lighter investigation task for a human. */
  Uncertain = 'uncertain',
  /**
   * A paid API refused us for billing — credits gone or a spend limit reached.
   * Never muted, never a code task: it pages a human, who has to top up.
   */
  Incident = 'incident',
}

/** Why an Error Center notification fires. Thrown errors: new/reopened. Alerts: every time. */
export type ErrorCenterNotifyReason = 'new' | 'reopened' | 'alert'

/** Payload of a `SystemError` notification. Optional because it is read back from stored jsonb. */
export interface ISystemErrorNotificationData {
  errorTriageId?: string
  title?: string
  origin?: string | null
  occurrenceCount?: number
  reason?: ErrorCenterNotifyReason
}

export const ERROR_TRIAGE_SOURCE_LABEL: Record<ErrorTriageSource, string> = {
  [ErrorTriageSource.Service]: 'Service',
  [ErrorTriageSource.Request]: 'Request',
  [ErrorTriageSource.UncaughtException]: 'Uncaught exception',
  [ErrorTriageSource.UnhandledRejection]: 'Unhandled rejection',
  [ErrorTriageSource.Alert]: 'Alert',
  [ErrorTriageSource.Client]: 'Client',
}

export const ERROR_TRIAGE_CLASSIFICATION_LABEL: Record<ErrorTriageClassification, string> = {
  [ErrorTriageClassification.Fixable]: 'Auto-fix',
  [ErrorTriageClassification.Uncertain]: 'Uncertain',
  [ErrorTriageClassification.Expected]: 'Expected',
  [ErrorTriageClassification.Incident]: 'Billing incident',
}

/**
 * How many recurrence comments we will append to a task's Recurrence subtask
 * before going quiet. The occurrence counter on the row keeps climbing either
 * way — this only caps Asana comment noise during a burst.
 */
export const MAX_RECURRENCE_APPENDS = 10

/**
 * How many times a fingerprint may be classified before the pipeline gives up
 * on it. Transient failures (Asana rate limits, model timeouts) deserve a
 * retry; a fingerprint that fails three times is failing for a real reason and
 * should stop burning model calls. A human can still force another attempt by
 * setting the row back to `pending` via `UpdateErrorTriage`.
 */
export const MAX_TRIAGE_ATTEMPTS = 3

/** How long individual occurrences are kept; the grouped row keeps its totals forever. */
export const ERROR_OCCURRENCE_RETENTION_DAYS = 30

/** One occurrence of a fingerprint — a row in the drawer's table and the Live tab. */
export interface IErrorOccurrence {
  id: string
  errorTriageId: string
  /** The fingerprint's display title, so a Live-tab row reads without the parent. */
  title: string
  occurredAt: string
  source: ErrorTriageSource
  origin: string
  message: string | null
  errorCode: string | null
  userName: string | null
  alertTitle: string | null
  params: Record<string, unknown> | null
  grafanaLink: string | null
}

export interface IErrorCenterListResponse {
  data: IErrorTriageListItem[]
  total: number
  /**
   * Whether the classifier runs in this environment. When it doesn't, an
   * unclassified row is "Not triaged", never "Triaging".
   */
  isTriageEnabled: boolean
}
